import { useEffect, useRef, useState } from "react";
import styles from "../styles/Home.module.css";
import {
	GAME_HEIGHT,
	GAME_WIDTH,
	GameState,
	Input,
	Status,
	createGame,
	loadHighScore,
	saveHighScore,
	step,
} from "../game/spaceShooter";

const KEY_MAP: Record<string, keyof Input> = {
	ArrowLeft: "left",
	ArrowRight: "right",
	ArrowUp: "up",
	ArrowDown: "down",
	" ": "fire",
};
const RESTART_DELAY_MS = 500;
const CONTROLS: { action: keyof Input; label: string; text: string }[] = [
	{ action: "up", label: "Move up", text: "↑" },
	{ action: "left", label: "Move left", text: "←" },
	{ action: "down", label: "Move down", text: "↓" },
	{ action: "right", label: "Move right", text: "→" },
	{ action: "fire", label: "Fire", text: "Fire" },
];

/** Leaves keyboard input to form and navigation controls. */
function isInteractive(target: EventTarget | null): boolean {
	return (
		target instanceof HTMLElement &&
		(target.isContentEditable ||
			target.closest("input, textarea, select, button, a") !== null)
	);
}

function draw(ctx: CanvasRenderingContext2D, state: GameState) {
	ctx.fillStyle = "#05060f";
	ctx.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);

	ctx.fillStyle = "#ffffff";
	for (const star of state.stars) {
		ctx.globalAlpha = star.alpha;
		ctx.fillRect(star.x, star.y, star.size, star.size);
	}
	ctx.globalAlpha = 1;

	ctx.fillStyle = "#ffd866";
	for (const b of state.bullets) {
		ctx.fillRect(b.x, b.y, b.width, b.height);
	}

	ctx.fillStyle = "#ff6188";
	for (const e of state.enemies) {
		ctx.beginPath();
		ctx.moveTo(e.x, e.y);
		ctx.lineTo(e.x + e.width, e.y);
		ctx.lineTo(e.x + e.width / 2, e.y + e.height);
		ctx.closePath();
		ctx.fill();
	}

	if (state.status !== "gameover") {
		const p = state.player;
		ctx.fillStyle = "#78dce8";
		ctx.beginPath();
		ctx.moveTo(p.x + p.width / 2, p.y);
		ctx.lineTo(p.x + p.width, p.y + p.height);
		ctx.lineTo(p.x + p.width / 2, p.y + p.height * 0.75);
		ctx.lineTo(p.x, p.y + p.height);
		ctx.closePath();
		ctx.fill();
	}
}

export default function HomePage() {
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const pointers = useRef(new Map<number, keyof Input>());
	const startGame = useRef(() => {});
	const [hud, setHud] = useState<{ score: number; highScore: number; status: Status }>(
		() => ({ score: 0, highScore: loadHighScore(), status: "ready" })
	);

	useEffect(() => {
		let state = createGame(loadHighScore());
		const keys = new Map<string, keyof Input>();
		let gameOverAt = 0;
		let frame = 0;
		let last = performance.now();
		const ctx = canvasRef.current?.getContext("2d") ?? null;

		const syncHud = () =>
			setHud((prev) =>
				prev.score === state.score &&
				prev.highScore === state.highScore &&
				prev.status === state.status
					? prev
					: { score: state.score, highScore: state.highScore, status: state.status }
			);

		startGame.current = () => {
			if (
				(state.status === "ready" ||
					(state.status === "gameover" &&
						performance.now() - gameOverAt >= RESTART_DELAY_MS))
			) {
				state = { ...createGame(state.highScore, Math.random, state.stars), status: "playing" };
				syncHud();
			}
		};
		const onKeyDown = (e: KeyboardEvent) => {
			const control = e.target instanceof HTMLElement
				? e.target.closest<HTMLButtonElement>("button[data-game-action]")
				: null;
			const action = control && (e.key === " " || e.key === "Enter")
				? control.dataset.gameAction as keyof Input
				: control || !isInteractive(e.target) ? KEY_MAP[e.key] : undefined;
			if (!action) return;
			e.preventDefault();
			keys.set(e.key, action);
			if (action === "fire" && !e.repeat && !control) startGame.current();
		};
		const onKeyUp = (e: KeyboardEvent) => {
			keys.delete(e.key);
		};
		const onBlur = () => {
			keys.clear();
			pointers.current.clear();
		};
		const onVisibilityChange = () => {
			if (document.hidden) onBlur();
		};
		const releasePointer = (e: PointerEvent) => {
			pointers.current.delete(e.pointerId);
		};

		const loop = (now: number) => {
			const dt = Math.min((now - last) / 1000, 0.05);
			last = now;
			const prev = state;
			const held = Array.from(keys.values()).concat(Array.from(pointers.current.values()));
			const input: Input = {
				left: held.includes("left"),
				right: held.includes("right"),
				up: held.includes("up"),
				down: held.includes("down"),
				fire: held.includes("fire"),
			};
			state = step(state, input, dt);
			// Persist immediately so a new high score survives a reload mid-game.
			if (state.highScore > prev.highScore) saveHighScore(state.highScore);
			if (state.status === "gameover" && prev.status !== "gameover") gameOverAt = now;
			if (ctx) draw(ctx, state);
			syncHud();
			frame = requestAnimationFrame(loop);
		};
		frame = requestAnimationFrame(loop);

		window.addEventListener("keydown", onKeyDown);
		window.addEventListener("keyup", onKeyUp);
		window.addEventListener("blur", onBlur);
		window.addEventListener("pointerup", releasePointer);
		window.addEventListener("pointercancel", releasePointer);
		document.addEventListener("visibilitychange", onVisibilityChange);
		return () => {
			cancelAnimationFrame(frame);
			window.removeEventListener("keydown", onKeyDown);
			window.removeEventListener("keyup", onKeyUp);
			window.removeEventListener("blur", onBlur);
			window.removeEventListener("pointerup", releasePointer);
			window.removeEventListener("pointercancel", releasePointer);
			document.removeEventListener("visibilitychange", onVisibilityChange);
			onBlur();
			startGame.current = () => {};
		};
	}, []);

	return (
		<div className={styles.wrapper}>
			<div className={styles.hud} aria-live="polite">
				<span>Score: {hud.score}</span>
				<span>High Score: {hud.highScore}</span>
			</div>
			<div className={styles.stage}>
				<canvas
					ref={canvasRef}
					className={styles.canvas}
					width={GAME_WIDTH}
					height={GAME_HEIGHT}
					role="img"
					aria-label="Space shooter game"
				/>
				{hud.status !== "playing" ? (
					<div className={styles.overlay}>
						<h2>{hud.status === "gameover" ? "Game Over" : "Space Shooter"}</h2>
						<p>Arrow keys to move, Space to shoot</p>
						<p>Press Space to {hud.status === "gameover" ? "restart" : "start"}</p>
						<button type="button" onClick={() => startGame.current()}>
							{hud.status === "gameover" ? "Restart" : "Start"}
						</button>
					</div>
				) : null}
			</div>
			<div className={styles.controls} role="group" aria-label="Game controls" aria-describedby="game-instructions">
				{CONTROLS.map(({ action, label, text }) => (
					<button
						key={action}
						type="button"
						className={styles[action]}
						data-game-action={action}
						aria-label={label}
						onPointerDown={(e) => {
							if (e.button !== 0) return;
							e.preventDefault();
							e.currentTarget.setPointerCapture(e.pointerId);
							pointers.current.set(e.pointerId, action);
						}}
						onPointerUp={(e) => pointers.current.delete(e.pointerId)}
						onPointerCancel={(e) => pointers.current.delete(e.pointerId)}
						onLostPointerCapture={(e) => pointers.current.delete(e.pointerId)}
						onContextMenu={(e) => e.preventDefault()}
					>
						{text}
					</button>
				))}
			</div>
			<p id="game-instructions" className={styles.instructions}>
				Hold arrows to move and Fire to shoot. Use multiple fingers to move and fire together.
				{" "}Keyboard: arrow keys and Space.
			</p>
		</div>
	);
}
